import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Col } from "react-bootstrap";
import { MCInput } from "../../MainComponents/Input/Input";
import { MCButton, MCSelect } from "../../MainComponents";
import { CloseIcon } from "../../Icon";
import { ReturnErrorMesages } from "./PreviewForm";

// Catalogo que define la relacion de la persona con la organizacion.
// Su valor decide si el tercer campo pide SOEID o Numero de cliente.
export const RELATIONSHIP_CATALOGUE = "RD-102101";

// Valores del catalogo que corresponden a gente ligada a la organizacion.
// Cualquier otro valor se considera externo => Numero de cliente.
export const SOEID_CATALOGUE_VALUES = [
  "employee_collaborator_teacher_union_member",
  "former_employee",
  "supplier_contractor",
  "exsupplier_excontractor",
];

// La llave del campo en formData es el id del catalogo, prefijado por su
// origen (ej. "C::EG::RD-102101"). Se compara el ultimo segmento para no
// confundirlo con catalogos cuyo id lo contenga como subcadena.
const isRelationshipKey = (key) =>
  String(key).split("::").pop() === RELATIONSHIP_CATALOGUE;

// El campo puede vivir en cualquier paso del formulario, asi que se busca en
// profundidad sobre todo el formData.
export const findRelationshipValue = (formData) => {
  let found = null;
  const walk = (node) => {
    if (found !== null || !node || typeof node !== "object") return;
    for (const [key, value] of Object.entries(node)) {
      if (isRelationshipKey(key) && typeof value === "string") {
        found = value;
        return;
      }
      walk(value);
    }
  };
  walk(formData);
  return found;
};

// El valor guardado viene prefijado ("type::employee_collaborator..."), por eso
// se compara por segmento; la subcadena queda como respaldo por si el formato
// del catalogo trae algun envoltorio extra.
export const usesSoeid = (formData) => {
  const value = findRelationshipValue(formData);
  if (!value) return false;
  const segments = value.split("::");
  return SOEID_CATALOGUE_VALUES.some(
    (option) => segments.includes(option) || value.includes(option)
  );
};

// Input de Involucrados: primero se elige el tipo y luego se capturan los datos
export const FormInputInvolved = ({
  schema,
  value,
  valid,
  br,
  origin,
  tryNext,
  handleChange,
  entireFormData,
}) => {
  const [t] = useTranslation("forms");
  const { lang: reduxLang } = useSelector((state) => state.lang);

  const lang = reduxLang || "es";

  const [typeInvolved, setTypeInvolved] = useState(null);
  const [nameInvolved, setNameInvolved] = useState("");
  const [positionInvolved, setPositionInvolved] = useState("");
  const [identifierInvolved, setIdentifierInvolved] = useState("");
  const [tryToAdd, setTryToAdd] = useState(false);

  const [orderedInvolved, setOrderedInvolved] = useState([]);

  const optionsInvolved = [
    { label: t("formPreview.option_victim"), value: "victim" },
    { label: t("formPreview.option_witness"), value: "witness" },
    { label: t("formPreview.option_offender"), value: "offender" },
    { label: t("formPreview.option_accomplice"), value: "accomplice" },
  ];

  // Interno => SOEID; externo o sin responder => Numero de cliente
  const isSoeid = useMemo(
    () => usesSoeid(entireFormData),
    [entireFormData]
  );

  const identifierPlaceholder = isSoeid
    ? t("formPreview.involved_soeid_placeholder")
    : t("formPreview.involved_client_number_placeholder");

  useEffect(() => {
    // Verificamos si value es null, undefined, o no es un array
    if (!value || !Array.isArray(value) || value?.length === 0) {
      setOrderedInvolved([]);
      return;
    }

    const tempVictims = [];
    const tempWitnesses = [];
    const tempOffenders = [];
    const tempAccomplice = [];

    for (const [idx, involved] of value.entries()) {
      if (involved.type === "victim") {
        tempVictims.push({ idx, ...involved });
      }
      if (involved.type === "witness") {
        tempWitnesses.push({ idx, ...involved });
      }
      if (involved.type === "offender") {
        tempOffenders.push({ idx, ...involved });
      }
      if (involved.type === "accomplice") {
        tempAccomplice.push({ idx, ...involved });
      }
    }

    setOrderedInvolved([
      ...tempVictims,
      ...tempWitnesses,
      ...tempOffenders,
      ...tempAccomplice,
    ]);
  }, [value]);

  const resetForm = () => {
    setTypeInvolved(null);
    setNameInvolved("");
    setPositionInvolved("");
    setIdentifierInvolved("");
    setTryToAdd(false);
  };

  const hdlAddInvolved = () => {
    setTryToAdd(true);
    if (typeInvolved === null) return;
    if (nameInvolved.trim() === "") return;
    if (positionInvolved.trim() === "") return;
    handleChange({
      target: {
        name: schema.key,
        type: "involved",
        action: "add",
        value: {
          type: typeInvolved,
          name: nameInvolved,
          // Se conserva la llave `occupation` (viaja al reporte y a la
          // investigacion); en pantalla la etiqueta ya dice "Puesto".
          occupation: positionInvolved,
          identifier: identifierInvolved,
          identifier_type: isSoeid ? "soeid" : "client_number",
        },
      },
    });
    resetForm();
  };

  const hdlDeleteInvolved = (idx) => {
    handleChange({
      target: {
        name: schema.key,
        type: "involved",
        action: "delete",
        idx,
      },
    });
  };

  const returnIsValid = (input) => {
    if (!tryToAdd) return true;
    if (input === "type" && typeInvolved === null) return false;
    if (input === "name" && nameInvolved.trim() === "") return false;
    if (input === "position" && positionInvolved.trim() === "") return false;
    return true;
  };

  return (
    <>
      <Col
        lg={schema.grid || 12}
        className={`preview-input-container form-input-involved`}
      >
        <label className={`${schema.required ? "label-required" : ""}`}>
          {schema.locales?.[lang] === undefined
            ? schema.label
            : schema.locales[lang]}
          :{schema.required ? " *" : ""}
        </label>

        {/* 1. Primero el tipo: define si se piden los demas datos */}
        <div className="form-input-involved__add-involved">
          <div className="flex-grow-1">
            <MCSelect
              type="text"
              name={`${origin}-involved-type`}
              options={optionsInvolved}
              isValid={returnIsValid("type")}
              value={
                optionsInvolved.find((item) => item.value === typeInvolved) ??
                null
              }
              onChange={(e) => setTypeInvolved(e.value)}
              placeholder={t("formPreview.involved_type_placeholder")}
            />
          </div>
        </div>

        {/* 2. Elegido el tipo, se capturan los datos de la persona */}
        {typeInvolved !== null && (
          <div className="form-input-involved__add-involved">
            <div className="flex-grow-1">
              <MCInput
                value={nameInvolved}
                onChange={(e) => setNameInvolved(e.target.value)}
                autoComplete="off"
                type="text"
                isValid={returnIsValid("name")}
                name={`${origin}-involved-name`}
                placeholder={t("formPreview.involved_name_placeholder")}
              />
            </div>

            <div className="flex-grow-1">
              <MCInput
                value={positionInvolved}
                onChange={(e) => setPositionInvolved(e.target.value)}
                autoComplete="off"
                type="text"
                isValid={returnIsValid("position")}
                name={`${origin}-involved-position`}
                placeholder={t("formPreview.involved_position_placeholder")}
              />
            </div>

            <div className="flex-grow-1">
              <MCInput
                value={identifierInvolved}
                onChange={(e) => setIdentifierInvolved(e.target.value)}
                autoComplete="off"
                type="text"
                name={`${origin}-involved-identifier`}
                placeholder={identifierPlaceholder}
              />
            </div>

            <div>
              <MCButton
                type="submit"
                label={t("formPreview.involved_addBtn")}
                variant="primary"
                outline
                onClick={hdlAddInvolved}
              />
            </div>
          </div>
        )}

        <div className="people-involved ">
          {orderedInvolved.length >= 1 ? (
            <>
              <div className="selectable-list">
                {orderedInvolved.map((item, idx) => (
                  <div key={idx} className={`selectable-item ${item.type}`}>
                    <strong>
                      {t(`formPreview.option_${item.type}`)}:&nbsp;
                    </strong>
                    {item.name} ({item.occupation})
                    {item.identifier ? ` · ${item.identifier}` : ""}{" "}
                    <button onClick={() => hdlDeleteInvolved(item.idx)}>
                      <CloseIcon color={"white"} size={20} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="not-involved">
              {t("formPreview.not_involved_added")}
            </div>
          )}
        </div>

        <ReturnErrorMesages tryNext={tryNext} valid={valid} />
      </Col>
      {br && <div></div>}
    </>
  );
};
